import { ApiError } from './http'

const GENERIC = new Set(['Request failed', 'Validation failed', 'Invalid input.'])

/** Flatten a DRF error payload ({field: [msg]}, [msg], 'msg') into plain sentences, never raw JSON. */
export function readableDetails(details: unknown): string {
  const parts: string[] = []
  const walk = (value: unknown) => {
    if (value == null || value === '') return
    if (Array.isArray(value)) { value.forEach(walk); return }
    if (typeof value === 'object') { Object.values(value as Record<string, unknown>).forEach(walk); return }
    const line = ruleText(String(value).trim())
    if (line && !parts.includes(line)) parts.push(line)
  }
  walk(details)
  return parts.join('；')
}

/** The message a business user should read for a failed request. */
export function apiErrorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const details = readableDetails(error.body.details)
    const message = error.message && !GENERIC.has(error.message) ? error.message : ''
    if (details && message && !details.includes(message)) return `${message}：${details}`
    return details || message || fallback
  }
  return error instanceof Error && error.message ? error.message : fallback
}

/** Plain-language reasons for rule codes the server returns without a sentence. */
const RULE_TEXT: Record<string, [string, string]> = {
  warehouse_and_finance_independent_nodes_required: [
    '盘点需两级审批：请在「审批流程」中为盘点配置两个节点（先仓库复核，再财务审批）。',
    'Stock count needs two approval levels: configure a warehouse review node and then a finance node.'],
  qualified_independent_count_reviewers_required: [
    '盘点审批节点没有可用的审批人：审批人不能是提交人，且需具备盘点复核／估值审批权限。',
    'A count approval node has no eligible approver (not the submitter, with count review/valuation rights).'],
  warehouse_and_finance_must_be_different_people: [
    '盘点的仓库复核与财务审批必须由两个不同的人负责。',
    'Warehouse review and finance approval of a count must be done by two different people.'],
  active_scoped_counter_required: [
    '所选初盘人或复盘人不能担任本次盘点：账号须在职，具备对应的初盘或独立复盘权限，已绑定本仓库，且数据范围能看到这张计划（「本人创建」看不到他人开立的盘点计划）。',
    'The chosen counter or recounter cannot take this plan. They must be active, hold the matching count permission, be bound to this warehouse, and be allowed to see this plan.'],
  independent_recounter_required: [
    '复盘人不能与初盘人相同。',
    'Recounter must be a different person from the first counter.'],
  controlled_position_required: [
    '本仓已启用货位管理，请选择物理货位；仓级管理的仓库请勿填写货位。',
    'This warehouse uses bin control — pick a physical bin; leave blank only for warehouse-level stock.'],
  physical_location_required: [
    '请选择有效的二级物理货位（须属于当前仓库）。',
    'Pick a valid level-2 physical bin that belongs to the current warehouse.'],
  physical_position_unavailable: [
    '所选货位不可用（已停用、已删除或状态不是可用）。',
    'The selected bin is unavailable (inactive, deleted, or not available).'],
  temperature_zone_mismatch: [
    '货位温区与商品默认温区不一致，请换到匹配温区的货位。',
    'Bin temperature zone does not match the product default zone.'],
}

/** Translate a known internal rule code; return the original string when unknown. */
export function ruleText(raw: string, english = false): string {
  const known = RULE_TEXT[raw]
  return known ? known[english ? 1 : 0] : raw
}

/** The specific reason behind a rejection, in the reader's language; '' when the server gave none. */
export function rejectionReason(error: unknown, english = false): string {
  if (!(error instanceof ApiError)) return ''
  const parts: string[] = []
  const walk = (value: unknown) => {
    if (value == null || value === '') return
    if (Array.isArray(value)) { value.forEach(walk); return }
    if (typeof value === 'object') { Object.values(value as Record<string, unknown>).forEach(walk); return }
    const raw = String(value).trim()
    const known = RULE_TEXT[raw]
    let line = known ? known[english ? 1 : 0] : raw
    if (!known && line.includes(' / ')) line = (line.split(' / ')[english ? 1 : 0] ?? line).trim()
    if (!known && /^[a-z0-9_]+$/.test(line)) return // Unmapped internal code: let the caller's guidance stand.
    if (line && !parts.includes(line)) parts.push(line)
  }
  walk(error.body.details)
  return parts.join(english ? '; ' : '；')
}
